import { useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import useSWR from "swr";
import { Basket } from "@features/cartDetails/types";
import { EventData, EventShowingsMsgs } from "@types";
import { fetcher } from "@utils/fetchers";
import { useLatestCookieValue } from "@utils/hooks";
import { getCookieValue } from "@utils/helpers";
import {
  getShowingsKey,
  useEventVenuePassword,
} from "@features/eventVenuePasswordGate";
import { Skeleton } from "@components/wrapper";
import Showings from "./components/showings";
import { Heading, Text } from "@components/theming";

interface Props {
  getPriceRange: ({
    pricesectionIds,
    groupedPricesectionIds,
  }: {
    pricesectionIds?: number[];
    groupedPricesectionIds?: number[];
  }) => { min: number; max: number; isEmpty?: boolean };
  event: EventData;
}

export default function EventCalendarMode({ getPriceRange, event }: Props) {
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const params = useSearchParams();
  const promo = params.get("promo");
  const eventVenuePassword = useEventVenuePassword();
  const eventId = event.legacy.e_id;
  const eventVenueId = event.legacy.ev_id;

  const t = useTranslations("EventPage.calendarmode");

  const pathname = usePathname();
  const draftCookie = getCookieValue("eventkey");
  const draftKey = pathname
    .split("/")
    .find((val) => val.startsWith("key-"))
    ?.replace("key-", "");

  const {
    data: showingData,
    isLoading,
    mutate,
  } = useSWR<EventShowingsMsgs>(
    event.status === "published" || (draftCookie && draftKey === draftCookie)
      ? getShowingsKey({
          eventId,
          eventVenueId,
          promo,
          password: eventVenuePassword,
        })
      : null,
    fetcher
  );

  const { data: basket } = useSWR<Basket>(
    basketKey ? `basket/${basketKey}` : null,
    fetcher
  );

  return showingData && showingData.showings.length > 0 ? (
    <Showings
      key={showingData.showings.map((showing) => showing.access.type).join("-")}
      showingData={showingData}
      basket={basket}
      event={event}
      getPriceRange={getPriceRange}
      mutate={mutate}
    />
  ) : isLoading ? (
    <div className="mt-10">
      <Skeleton className="h-9 w-36 bg-gray-500 rounded-md" />
      <div>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="rounded-md h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  ) : showingData && showingData.showings.length === 0 ? (
    <div className="w-full flex flex-col items-center gap-4 p-4 sm:py-8">
      <Heading as="h2" sharedClasses="text-3xl font-bold tracking-tight">
        {t("error.nodates.heading")}
      </Heading>
      <Text sharedClasses="text-lg">{t("error.nodates.description")}</Text>
    </div>
  ) : (
    <div className="w-full flex flex-col items-center gap-4 p-4 sm:py-8">
      <Heading as="h2" sharedClasses="text-3xl font-bold tracking-tight">
        {t("error.notavailable.heading")}
      </Heading>
      <Text sharedClasses="text-lg">{t("error.notavailable.description")}</Text>
    </div>
  );
}
